import React, { Component } from "react";
import { observer } from "mobx-react";
import { computed } from "mobx";
import { withTranslation } from "react-i18next";
import { Checkbox, Form, Grid, Message, Select } from "semantic-ui-react";
import PropTypes from "prop-types";
import Customer from "~/models/Customer";
import FormStore from "~/stores/FormStore";
import { getValidationErrors } from "~/utils/ReactReformed/ValidateSchema";
import TourUiMeet3dLevelOptions from "~/constants/Tour/TourUiMeet3dLevelOptions";
import ImageUpload from "~/components/element/ImageUpload/ImageUpload";
import UiMeet3dPhotoType from "~/constants/UiMeet3d/UiMeet3dPhotoType";
import UiMeet3dLevel from "~/constants/UiMeet3d/UiMeet3dLevel";
import Provider from "~/models/Provider";
import FormHelper from "~/utils/FormHelper";
import UiMeet3dPhotoStore from "~/stores/UiMeet3d/UiMeet3dPhotoStore";
import "~/modules/UiMeet3d/styles/UiMeet3dGeneralForm.less";

@observer
class UiMeet3dGeneralForm extends Component {
  @computed
  get providerUiMeet3dLevelDefaultText() {
    const { t, actualUser } = this.props;

    if (
      typeof actualUser.providerDefaults !== "undefined" &&
      typeof actualUser.providerDefaults.uiMeet3dLevel !== "undefined"
    ) {
      const defaultOption = TourUiMeet3dLevelOptions().find(
        option => option.value === actualUser.providerDefaults.uiMeet3dLevel,
      );

      if (typeof defaultOption === "undefined") {
        return actualUser.providerDefaults.uiMeet3dLevel;
      }

      return t(defaultOption.text);
    }

    return "";
  }

  @computed
  get modelUiMeet3dLevelCoalesced() {
    const { actualUser, formStore } = this.props;
    const { model } = formStore;

    if (
      this.isCustomer &&
      FormHelper.convertToValue(model.uiMeet3dLevel) === null &&
      actualUser.providerDefaults &&
      actualUser.providerDefaults.uiMeet3dLevel
    ) {
      return actualUser.providerDefaults.uiMeet3dLevel;
    }

    return parseInt(model.uiMeet3dLevel, 10);
  }

  @computed
  get isProvider() {
    const { actualUser } = this.props;

    return actualUser instanceof Provider;
  }

  @computed
  get isCustomer() {
    const { actualUser } = this.props;

    return actualUser instanceof Customer;
  }

  render() {
    const { formStore, t, validationErrors, actualUser, uiMeet3dPhotoStore } = this.props;
    const { setProperty, getAggregatedError, model } = formStore;
    const { uiMeet3dPhotos } = uiMeet3dPhotoStore;

    const validationRules = {};
    const validation = getValidationErrors(validationRules, model);

    const mainUserAvatarPhotoPathWeb = uiMeet3dPhotoStore.getUiMeet3dPhotoPathWeb(
      uiMeet3dPhotos,
      UiMeet3dPhotoType.MAIN_USER_AVATAR,
    );
    const canDeleteMainUserAvatarPhoto = uiMeet3dPhotoStore.canDeleteUiMeet3dPhoto(
      uiMeet3dPhotos,
      UiMeet3dPhotoType.MAIN_USER_AVATAR,
    );

    const mainUserBackBadgePhotoPathWeb = uiMeet3dPhotoStore.getUiMeet3dPhotoPathWeb(
      uiMeet3dPhotos,
      UiMeet3dPhotoType.MAIN_USER_BACK_BADGE,
    );
    const canDeleteMainUserBackBadgePhoto = uiMeet3dPhotoStore.canDeleteUiMeet3dPhoto(
      uiMeet3dPhotos,
      UiMeet3dPhotoType.MAIN_USER_BACK_BADGE,
    );

    const finalDefaultMainUserAvatarPhotoPathWeb =
      mainUserAvatarPhotoPathWeb ||
      (typeof actualUser.photoUrl !== "undefined" && actualUser.photoUrl !== null && actualUser.photoPathWeb) ||
      "/tourbuilder/assets/images/user_sil.jpg";
    const finalDefaultMainUserBackBadgePhotoPathWeb =
      mainUserBackBadgePhotoPathWeb ||
      (typeof actualUser.logoUrl !== "undefined" && actualUser.logoUrl !== null && actualUser.logoPathWeb) ||
      "/tourbuilder/assets/images/user_sil.jpg";

    return (
      <Form style={{ marginTop: "1rem" }} className="ui-meet-3d-general-form">
        <Grid columns="equal">
          <Grid.Column>
            <div className="image-upload">
              <h4>{t("clientPanelMisc:avatar")}</h4>
              <Checkbox
                disabled={!canDeleteMainUserAvatarPhoto}
                label={t("clientPanelInfo:clickDelete")}
                checked={canDeleteMainUserAvatarPhoto && model.deleteMainUserAvatarPhoto}
                onChange={() => setProperty("deleteMainUserAvatarPhoto", !model.deleteMainUserAvatarPhoto)}
              />
              <ImageUpload
                onFileSelected={encoded => setProperty("mainUserAvatarPhotoData", encoded)}
                defaultSrc={finalDefaultMainUserAvatarPhotoPathWeb}
                accept={["image/jpeg"]}
              />
            </div>
          </Grid.Column>
          <Grid.Column>
            <div className="image-upload">
              <h4>{t("clientPanelMisc:backBadge")}</h4>
              <Checkbox
                disabled={!canDeleteMainUserBackBadgePhoto}
                label={t("clientPanelInfo:clickDelete")}
                checked={canDeleteMainUserBackBadgePhoto && model.deleteMainUserBackBadgePhoto}
                onChange={() => setProperty("deleteMainUserBackBadgePhoto", !model.deleteMainUserBackBadgePhoto)}
              />
              <ImageUpload
                onFileSelected={encoded => setProperty("mainUserBackBadgePhotoData", encoded)}
                defaultSrc={finalDefaultMainUserBackBadgePhotoPathWeb}
                accept={["image/jpeg"]}
              />
              <p>* {t("clientPanelMisc:backBadgeExplanation")}</p>
            </div>
          </Grid.Column>
        </Grid>
        <br />
        {this.modelUiMeet3dLevelCoalesced !== UiMeet3dLevel.VIDEO && (
          <Message negative>
            <p>{t("clientPanelMisc:uiMeet3dLevelNotOptimal")}</p>
          </Message>
        )}
        {this.isProvider && (
          <Form.Field
            control={Select}
            name="uiMeet3dLevel"
            label={t("clientPanelMisc:uiMeet3dLevel")}
            value={model.uiMeet3dLevel}
            options={TourUiMeet3dLevelOptions().map(mode => {
              return {
                key: mode.key,
                value: FormHelper.convertToString(mode.value),
                text: typeof mode.text === "object" ? t(mode.text[0], mode.text[1]) : t(mode.text),
              };
            })}
            onChange={(_event, { value }) => setProperty("uiMeet3dLevel", value)}
            error={getAggregatedError("uiMeet3dLevel", validation, validationErrors)}
          />
        )}
        {this.isCustomer && (
          <Form.Field
            control={Select}
            name="uiMeet3dLevel"
            label={t("clientPanelMisc:uiMeet3dLevel")}
            value={model.uiMeet3dLevel}
            options={TourUiMeet3dLevelOptions([
              "clientPanelConstants:useProviderSettingWithValue",
              {
                defaultValue: this.providerUiMeet3dLevelDefaultText,
              },
            ]).map(mode => {
              return {
                key: mode.key,
                value: mode.value,
                text: typeof mode.text === "object" ? t(mode.text[0], mode.text[1]) : t(mode.text),
              };
            })}
            onChange={(_event, { value }) => setProperty("uiMeet3dLevel", value)}
            error={getAggregatedError("uiMeet3dLevel", validation, validationErrors)}
          />
        )}
      </Form>
    );
  }
}

UiMeet3dGeneralForm.propTypes = {
  actualUser: PropTypes.oneOfType([PropTypes.instanceOf(Provider), PropTypes.instanceOf(Customer)]),
  formStore: PropTypes.instanceOf(FormStore).isRequired,
  t: PropTypes.func.isRequired,
  validationErrors: PropTypes.object.isRequired,
  uiMeet3dPhotoStore: PropTypes.instanceOf(UiMeet3dPhotoStore),
};

export default withTranslation()(UiMeet3dGeneralForm);
